import { KeyRound, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { ServiceUserKey } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { Input } from "@/components/ui/input";
import { SectionCard } from "@/components/ui/section-card";
import { ApiKeyRevealDialog } from "@/features/service-users/components/ApiKeyRevealDialog";
import {
  useCreateServiceUserApiKey,
  useRevokeServiceUserApiKey,
  useServiceUserApiKeys,
} from "@/features/service-users/hooks/useServiceUserApiKeys";

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

export function ServiceUserApiKeysPanel({
  serviceUserId,
}: {
  serviceUserId: string;
}) {
  const { t } = useTranslation("serviceUsers");
  const keysQuery = useServiceUserApiKeys(serviceUserId);
  const createKey = useCreateServiceUserApiKey(serviceUserId);
  const [name, setName] = useState("");
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revealOpen, setRevealOpen] = useState(false);

  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const created = await createKey.mutateAsync({ name: trimmed });
      setName("");
      setRevealed(created.key);
      setRevealOpen(true);
      toast.success(t("toasts.keyCreated"));
    } catch (err) {
      toastApiError(err);
    }
  }

  const keys = keysQuery.data ?? [];

  return (
    <SectionCard title={t("keys.title")} description={t("keys.description")}>
      <form onSubmit={handleGenerate} className="flex flex-wrap items-end gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("keys.namePlaceholder")}
          maxLength={120}
          className="max-w-xs"
        />
        <Button type="submit" disabled={createKey.isPending || !name.trim()}>
          <Plus className="h-4 w-4" />
          {t("keys.generate")}
        </Button>
      </form>

      {keys.length === 0 ? (
        <p className="text-sm text-ink-500">{t("keys.empty")}</p>
      ) : (
        <ul className="divide-y divide-line rounded-xl border border-line">
          {keys.map((key) => (
            <ApiKeyRow key={key.id} serviceUserId={serviceUserId} apiKey={key} />
          ))}
        </ul>
      )}

      <ApiKeyRevealDialog
        open={revealOpen}
        onOpenChange={setRevealOpen}
        apiKey={revealed}
      />
    </SectionCard>
  );
}

function ApiKeyRow({
  serviceUserId,
  apiKey,
}: {
  serviceUserId: string;
  apiKey: ServiceUserKey;
}) {
  const { t } = useTranslation("serviceUsers");
  const revoke = useRevokeServiceUserApiKey(serviceUserId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const lastUsed = formatDate(apiKey.last_used_at);
  const expires = formatDate(apiKey.expires_at);

  async function handleRevoke() {
    try {
      await revoke.mutateAsync(apiKey.id);
      toast.success(t("toasts.keyRevoked"));
      setConfirmOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="flex items-center gap-2 font-medium text-ink-950">
          <KeyRound className="h-4 w-4 text-ink-500" />
          {apiKey.name}
          {!apiKey.is_active ? (
            <Badge variant="destructive">{t("keys.revoked")}</Badge>
          ) : null}
        </span>
        <span className="font-mono text-xs text-ink-500">
          {apiKey.key_prefix}…
        </span>
        <span className="text-xs text-ink-500">
          {t("keys.lastUsed")}: {lastUsed ?? t("keys.neverUsed")} ·{" "}
          {t("keys.expires")}: {expires ?? t("keys.noExpiry")}
        </span>
      </div>
      {apiKey.is_active ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setConfirmOpen(true)}
          disabled={revoke.isPending}
        >
          <Trash2 className="h-4 w-4" />
          {t("keys.revoke")}
        </Button>
      ) : null}

      <ConfirmDestructiveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("keys.revokeConfirm.title")}
        description={t("keys.revokeConfirm.description")}
        resourceName={apiKey.name}
        resourceKind={t("keys.revokeConfirm.resourceKind")}
        confirmLabel={t("keys.revokeConfirm.confirm")}
        onConfirm={handleRevoke}
        isPending={revoke.isPending}
      />
    </li>
  );
}
